import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import ImageUploadZone from '../../proof-submission/components/ImageUploadZone';
import { supabase } from '../../../lib/supabaseClient';
import { compressImageUnderBytes } from '../../../utils/imageCompression';
import { getCachedSignedUrl, makeSafeObjectPath, uploadToBucket } from '../../../utils/storage';
import { useAuth } from '../../../auth/AuthProvider';

const ImagePersonaSettings = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const canUse = useMemo(() => !!user?.id, [user?.id]);

  const load = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError('');
    try {
      const { data, error: qErr } = await supabase
        .from('ch_personas')
        .select('id, name, image_path, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (qErr) {
        setError(qErr.message || '인물 목록을 불러오지 못했습니다');
        setItems([]);
        return;
      }

      const vm = await Promise.all(
        (data || []).map(async (r) => {
          const url = r.image_path ? await getCachedSignedUrl({ bucket: 'persona-images', path: r.image_path }) : '';
          return {
            id: r.id,
            name: r.name,
            imagePath: r.image_path,
            imageUrl: url,
            createdAt: r.created_at,
          };
        })
      );

      setItems(vm);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!canUse) return;
    load();
  }, [canUse, load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setSelectedImage(null);
    setFormError('');
    setIsEditorOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item?.name || '');
    setSelectedImage(item?.imageUrl ? { file: null, preview: item.imageUrl } : null);
    setFormError('');
    setIsEditorOpen(true);
  };

  const validate = () => {
    if (!name.trim()) {
      setFormError('이름을 입력해주세요');
      return false;
    }
    if (!selectedImage?.preview) {
      setFormError('이미지를 선택해주세요');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleSave = async () => {
    if (!user?.id) return;
    if (!validate()) return;

    setIsSaving(true);
    setFormError('');

    try {
      const isEdit = !!editing?.id;

      const previousPath = isEdit ? (editing?.imagePath || '') : '';

      let nextPath = previousPath;
      if (selectedImage?.file) {
        const file = selectedImage.file;
        let blob = file;
        if (file.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(file, 1024 * 1024);
        }

        const path = makeSafeObjectPath({ userId: user.id, originalName: file?.name, prefix: 'personas' });
        const { error: uploadError } = await uploadToBucket({
          bucket: 'persona-images',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          setFormError(uploadError?.message || '이미지 업로드에 실패했습니다');
          return;
        }

        nextPath = path;
      }

      if (!nextPath) {
        setFormError('이미지 경로가 비어 있습니다');
        return;
      }

      if (isEdit) {
        const { error: updErr } = await supabase
          .from('ch_personas')
          .update({ name: name.trim(), image_path: nextPath })
          .eq('id', editing.id)
          .eq('user_id', user.id);

        if (updErr) {
          setFormError(updErr?.message || '수정에 실패했습니다');
          return;
        }
      } else {
        const { error: insErr } = await supabase
          .from('ch_personas')
          .insert({ user_id: user.id, name: name.trim(), image_path: nextPath });

        if (insErr) {
          setFormError(insErr?.message || '등록에 실패했습니다');
          return;
        }
      }

      if (previousPath && previousPath !== nextPath) {
        await supabase.storage.from('persona-images').remove([previousPath]);
      }

      setIsEditorOpen(false);
      setEditing(null);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!user?.id || !item?.id) return;

    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    setIsLoading(true);
    setError('');
    try {
      const { error: delErr } = await supabase
        .from('ch_personas')
        .delete()
        .eq('id', item.id)
        .eq('user_id', user.id);

      if (delErr) {
        setError(delErr?.message || '삭제에 실패했습니다');
        return;
      }

      if (item?.imagePath) {
        await supabase.storage.from('persona-images').remove([item.imagePath]);
      }

      await load();
    } finally {
      setIsLoading(false);
    }
  };

  if (!canUse) {
    return (
      <div className="bg-card rounded-lg shadow-gaming-md p-6">
        <p className="text-sm text-muted-foreground caption">로그인이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg shadow-gaming-md p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg md:text-xl font-heading font-semibold text-foreground">이미지 생성 관리</div>
            <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
              AI 이미지 생성 시 합성할 인물을 등록/관리합니다
            </div>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={openCreate}>
            인물 추가
          </Button>
        </div>

        {error ? (
          <div className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 p-3">
            <div className="text-xs md:text-sm text-destructive">{error}</div>
          </div>
        ) : null}

        {isLoading ? (
          <div className="mt-4 text-sm text-muted-foreground caption">로딩 중...</div>
        ) : items.length === 0 ? (
          <div className="mt-4 text-sm text-muted-foreground caption">등록된 인물이 없습니다.</div>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <div key={it.id} className="rounded-lg border border-border bg-background overflow-hidden">
                <div className="aspect-video bg-muted">
                  {it.imageUrl ? (
                    <img src={it.imageUrl} alt={it.name} className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="p-4">
                  <div className="text-sm font-heading font-semibold text-foreground truncate">{it.name}</div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <Button variant="outline" iconName="Edit" iconPosition="left" onClick={() => openEdit(it)}>
                      수정
                    </Button>
                    <Button variant="destructive" iconName="Trash2" iconPosition="left" onClick={() => handleDelete(it)}>
                      삭제
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isEditorOpen && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  {editing ? '인물 수정' : '인물 추가'}
                </div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">이름과 이미지를 입력하세요</div>
              </div>
              <button
                type="button"
                onClick={() => (isSaving ? null : setIsEditorOpen(false))}
                className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                type="text"
                label="이름"
                value={name}
                onChange={(e) => setName(e?.target?.value)}
                maxLength={50}
                required
              />

              <div>
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  이미지 <span className="text-destructive">*</span>
                </label>
                <ImageUploadZone
                  title="인물 이미지 업로드"
                  selectedImage={selectedImage}
                  onImageSelect={(img, err) => {
                    setSelectedImage(img);
                    if (err) setFormError(err);
                    else setFormError('');
                  }}
                  error={null}
                />
              </div>

              {formError ? (
                <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3">
                  <div className="text-xs md:text-sm text-destructive">{formError}</div>
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setIsEditorOpen(false)} disabled={isSaving}>
                  취소
                </Button>
                <Button variant="default" iconName="Check" iconPosition="left" onClick={handleSave} loading={isSaving}>
                  저장
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePersonaSettings;
